import React, { useEffect, useRef, useState } from 'react'
import { Accordion, Checkbox, Modal, SvgError, TENANTS } from '@ui-kit'
import {
  FieldValues,
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import {
  addToaster,
  calculateFPL,
  ErrorResponse,
  getGenericEntityListingAPI,
  GetGenericEntityListingApiType,
  getLayoutByIdApi,
  LayoutResponseType,
  SiteSettingsType,
  useDispatch,
  WorkflowInstanceTaskType,
  WorkflowTaskIdentifierEntityLayoutInfoType,
} from '@app-store'
import { HybridLayoutMultiLookupRow } from '../hybird-layout/hybird-lookup/HybridLayoutLookupRow'
import {
  editEntityData,
  getEntityDataByIdEmbedded,
} from '../../apis/entityData.api'
import { isEmpty } from 'lodash'
import { RootState, useAppSelector } from '../../utils/agent.store'
import { handleExperian } from '../../utils/experian'
import { PaysignErrorType } from '../../types/common'
import { AxiosError } from 'axios'
import { v4 as uuid } from 'uuid'
import PaySignErrorPopup from './PaySignErrorPopup'
import {
  ENTITY_FIELDS,
  ENTITY_NAME,
  MODAL_POPUP_ACTIONS,
  PAP_WORKFLOW_CONSTANTS,
  WORKFLOW_STATUS,
} from '../../utils/constants'
import { SelectButtonDropDown } from './SelectButtonDropDown'
import { PAP_CONSTANTS, PAP_NAMESPACES } from '../../utils/pap.constants'
import {
  ColumnValueComponentProps,
  IncomeSectionComponentProps,
  IncomeTaskTablesComponentProps,
} from '../../types/pap.types'
import { permissionObject } from '../../utils/helperFunctions'
import { STRINGS } from '../../utils/strings'

const IncomeContainerList: FieldValues[] = [
  {
    id: 'ea1b007b-b984-4184-8940-dfe548cffcf8',
    key: 'finalResults',
    label: PAP_NAMESPACES?.FINAL_RESULTS,
  },
  {
    id: '8e4706c2-cce9-4831-a044-919810f48f87',
    type: 'layout',
    key: ENTITY_FIELDS.case.patient,
    layoutId: '7848',
    entityId: '18',
    layoutInfoKey: 'patient',
    isEIncome: true,
  },
  {
    id: '5e7fdf45-1e0e-4e8e-81ca-4f7a61b4bfa9',
    key: 'applicantResults',
    label: PAP_NAMESPACES?.APPLICATION_RESULTS,
  },
  {
    id: 'b0ced8a3-dc97-4b7d-ab0f-0dc7cee7dafc',
    type: 'layout',
    layoutId: '7888',
    layoutInfoKey: 'case',
    entityId: '32',
  },
  {
    id: '16234008-2ef4-4104-9d4d-026e5ab8bf21',
    key: 'poiResults',
    label: PAP_NAMESPACES?.POI_RESULTS,
  },
]

const IncomeCaseSubTotalKeys: Record<string, string[]> = {
  c_case_income_details_case: [
    ENTITY_FIELDS.c_case_income_details_case.annual_amount,
  ],
  c_medical_expense_details_case: [
    ENTITY_FIELDS.c_medical_expense_details_case.amount,
  ],
}

const valueFormatter = ({ type, value }: { type: string; value: number }) => {
  if (type === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value)
  }
  if (type === 'percentage') {
    return value != null ? value.toFixed(2) + '%' : '-'
  }
  return value
}

// Consolidated constant containing sections and their key definitions
const IncomeConstants: FieldValues = {
  finalResults: {
    id: 'finalResults',
    label: PAP_NAMESPACES?.FINAL_RESULTS,
    keys: [
      {
        label: PAP_NAMESPACES?.HOUSEHOLD_SIZE,
        key: PAP_CONSTANTS?.HOUSEHOLD_SIZE_FINAL,
        type: 'number',
        isDisabled: true,
        isView: true,
      },
      {
        label: PAP_NAMESPACES?.ANNUAL_HOUSEHOLD_INCOME,
        key: PAP_CONSTANTS?.ANNUAL_INCOME_FINAL,
        type: 'currency',
        isDisabled: true,
        isView: true,
      },
      {
        label: PAP_NAMESPACES?.FPL_PERCENTAGE,
        key: PAP_CONSTANTS?.FPL_PERCENTAGE_FINAL,
        type: 'percentage',
        isDisabled: true,
        isView: true,
      },
    ],
  },
  poiResults: {
    id: 'poiResults',
    label: PAP_NAMESPACES?.POI_RESULTS,
    keys: [
      {
        label: PAP_NAMESPACES?.HOUSEHOLD_SIZE,
        key: PAP_CONSTANTS?.HOUSEHOLD_SIZE_POI,
        type: 'number',
      },
      {
        label: PAP_NAMESPACES?.ANNUAL_HOUSEHOLD_INCOME,
        key: PAP_CONSTANTS?.ANNUAL_INCOME_POI,
        type: 'currency',
        isDisabled: true,
        isView: true,
      },
      {
        label: PAP_NAMESPACES?.FPL_PERCENTAGE,
        key: PAP_CONSTANTS?.FPL_PERCENTAGE_POI,
        type: 'percentage',
        isDisabled: true,
        isView: true,
      },
    ],
  },
  applicantResults: {
    id: 'applicantResults',
    label: PAP_NAMESPACES?.APPLICATION_HOUSEHOLD_INCOME,
    keys: [
      {
        label: PAP_NAMESPACES?.HOUSEHOLD_SIZE,
        key: PAP_CONSTANTS?.HOUSEHOLD_SIZE_APPLICATION,
        type: 'number',
      },
      {
        label: PAP_NAMESPACES?.ANNUAL_HOUSEHOLD_INCOME,
        key: PAP_CONSTANTS?.ANNUAL_INCOME_ON_APPLICATION,
        type: 'currency',
      },
      {
        label: PAP_NAMESPACES?.FPL_PERCENTAGE,
        key: PAP_CONSTANTS?.FPL_PERCENTAGE_APPLICATION,
        type: 'percentage',
        isDisabled: true,
        isView: true,
      },
    ],
    NOVO_PAP_E_INCOME_TASK_SUB_UI_IDENTIFIER: {
      label: PAP_NAMESPACES?.APPLICATION_HOUSEHOLD_SIZE,
      keys: [
        {
          label: PAP_NAMESPACES?.HOUSEHOLD_SIZE,
          key: PAP_CONSTANTS?.HOUSEHOLD_SIZE_APPLICATION,
          type: 'number',
        },
      ],
    },
  },
} as const

export const IncomeLayoutCaseComponent: React.FC<{
  items: FieldValues[]
  disableAction?: boolean
  fetchAgain: ({
    fieldName,
    value,
  }: {
    fieldName: string
    value: string
  }) => void
}> = ({ items, fetchAgain, disableAction }) => {
  return (
    <div className="mx-4 my-2">
      <table className={'min-w-full border border-white  text-sm'}>
        <thead className="bg-secondary-100">
          <tr>
            {items.map((col) => (
              <td
                key={col.key}
                className={'bg-secondary-50 py-2 px-2'}
                scope="col"
              >
                {col.label}
              </td>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="bg-white">
            {items.map((col, index) => (
              <ColValueComponent
                key={`col-${index}`}
                parentKey={ENTITY_FIELDS.case.case_pap_details}
                col={col}
                isView={disableAction || col?.isView}
                fetchAgain={fetchAgain}
              />
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  )
}

const ColValueComponent = ({
  parentKey,
  col,
  isView,
  fetchAgain,
}: ColumnValueComponentProps) => {
  const { register } = useFormContext()

  const fieldKey = parentKey ? `${parentKey}.${col.key}` : col.key
  const fieldValue = useWatch({ name: fieldKey })

  return (
    <td key={col.key} className={` py-2 px-2 whitespace-nowrap w-auto`}>
      {isView ? (
        <span>
          {fieldValue !== undefined && fieldValue !== null
            ? valueFormatter({ type: col.type, value: fieldValue })
            : '--'}
        </span>
      ) : (
        (() => {
          const reg = register(fieldKey)
          return (
            <input
              type="number"
              {...reg}
              disabled={col?.isDisabled}
              onChange={(e) => {
                reg.onChange(e)
                const value = e.target.value
                if ((e.target as FieldValues).__debounceTimer) {
                  clearTimeout((e.target as FieldValues).__debounceTimer)
                }
                ;(e.target as FieldValues).__debounceTimer = setTimeout(() => {
                  fetchAgain({
                    fieldName: col.key,
                    value,
                  })
                }, 500)
              }}
              className={` border px-2 py-2 rounded outline-none focus:ring-2 focus:ring-blue-200 focus:border-transparent ${
                col?.isDisabled
                  ? 'bg-tertiary-100 cursor-not-allowed'
                  : 'bg-white'
              }`}
            />
          )
        })()
      )}
    </td>
  )
}

const IncomeSectionComponent: React.FC<IncomeSectionComponentProps> = ({
  section,
  isDiscrepancy,
  fetchAgain,
  disableAction,
  subUiIdentifier = '',
}) => {
  const getHeader = () => {
    return (
      <div className="flex items-center gap-3 justify-start">
        <span>
          {subUiIdentifier
            ? section?.[subUiIdentifier]?.label || section?.label
            : section?.label}
        </span>
        {isDiscrepancy && section?.id === 'finalResults' ? (
          <span>
            <SvgError className="w-4 h-4" />
          </span>
        ) : null}
      </div>
    )
  }

  return (
    <Accordion
      key={section.id}
      isDefaultActive={true}
      heading={getHeader()}
      alwaysOpen={true}
    >
      <IncomeLayoutCaseComponent
        items={
          subUiIdentifier
            ? section?.[subUiIdentifier]?.keys || section?.keys
            : section?.keys
        }
        disableAction={disableAction}
        fetchAgain={fetchAgain}
      />
    </Accordion>
  )
}

const IncomeTaskTablesComponent = ({
  section,
  recordId,
  parentKey,
  setSubtotalDataObject,
  calculateAndFetch,
  layoutInfo,
  disableAction,
  task,
  isAppealTask,
}: IncomeTaskTablesComponentProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [layoutObject, setLayoutObject] = useState<LayoutResponseType | null>(
    null
  )
  const clientId = useAppSelector(
    (state: RootState) => state.authUser?.selectedClientTenant?.id
  )

  const isEIncomeAllowedForTenant = clientId !== TENANTS.NOVO
  const fieldValue = useWatch({
    name: parentKey ? `${parentKey}.id` : '___NULL___',
  })
  const dispatch = useDispatch()
  const { workflowDefinition } = useAppSelector((state) => state?.workflowData)
  const paySignErrorInitialState: PaysignErrorType = {
    flag: false,
    heading: null,
    message: null,
  }
  const [paysignErrorPopup, setPaysignErrorPopup] = useState<PaysignErrorType>(
    paySignErrorInitialState
  )
  const [refetchKey, setRefetchKey] = useState<number>(0)

  const fetchLayout = async () => {
    setIsLoading(true)
    const layoutId = layoutInfo?.layout_id ?? section?.layoutId
    const entityId = layoutInfo?.entity_id ?? section?.entityId
    try {
      const response = await getLayoutByIdApi({ layoutId, entityId })
      if (response?.data) {
        setLayoutObject(response.data)
      }
    } catch (err) {
      console.error('Error fetching layout:', err)
    } finally {
      setIsLoading(false)
    }
  }
  const handlePaysignError = async (error: AxiosError) => {
    const errorMessage = error?.response?.data as ErrorResponse
    if (errorMessage?.message) {
      dispatch(
        addToaster({
          message: errorMessage?.message,
          type: 'error',
          id: uuid(),
        })
      )
    }
  }
  const handleEIncomeClick = async () => {
    try {
      const response = await getEntityDataByIdEmbedded(
        workflowDefinition?.global_variables?.entity_name ?? '',
        Number(workflowDefinition?.global_variables?.entity_id),
        ['patient', 'products'],
        Number(recordId)
      )
      if (response?.data) {
        const patientIdVal = response?.data?.patient?.id
        const res = await handleExperian(patientIdVal, setPaysignErrorPopup)
        if (res) {
          await calculateAndFetch()
          setRefetchKey((prev) => prev + 1)
        }
      }
    } catch (error) {
      await handlePaysignError(error as AxiosError)
    }
  }

  const handleSubtotalChange = (_key: string, subTotal: FieldValues) => {
    setSubtotalDataObject((prev: FieldValues) => ({
      amount: prev.amount || 0,
      annual_amount: prev.annual_amount || 0,
      ...subTotal,
    }))
  }

  useEffect(() => {
    fetchLayout()
  }, [])

  if (isLoading) {
    return <div>{PAP_NAMESPACES?.LOADING}</div>
  }
  if (!layoutObject) {
    return <div>{PAP_NAMESPACES?.NO_LAYOUT_FOUND}</div>
  }
  return layoutObject?.layout_data?.pages?.[0]?.rows?.map((row) => (
    <React.Fragment key={row.rowId}>
      <div id={`income_task_form_${row.rowId}`} />
      {row?.rowType === 'lookup_listing' ? (
        <HybridLayoutMultiLookupRow
          isTaskCompleted={disableAction}
          row={row}
          recordId={parentKey ? fieldValue : recordId}
          fullLayout={layoutObject}
          formUniqueId={`income_task_form_${row.rowId}`}
          isDefaultOpen={true}
          parentKey={parentKey}
          handleSubtotalChange={handleSubtotalChange}
          alwaysOpen={true}
          refetchLogic={() => {
            console.log('refetchLogic')
          }}
          showEincome={!!section?.isEIncome && isEIncomeAllowedForTenant}
          handleEIncomeClick={handleEIncomeClick}
          key={refetchKey?.toString()}
          subtotalkeys={
            IncomeCaseSubTotalKeys[row?.lookup_meta?.lookup_keyName ?? ''] ?? []
          }
          task={task}
          isMinHeight={false}
          isAppealTask={isAppealTask}
        />
      ) : null}
      {paysignErrorPopup.flag && (
        <Modal
          isOpen={paysignErrorPopup.flag}
          closeModal={() => setPaysignErrorPopup(paySignErrorInitialState)}
          width={'w-fit max-w-[98%]'}
        >
          <PaySignErrorPopup
            message={paysignErrorPopup.message}
            heading={paysignErrorPopup.heading}
            list={paysignErrorPopup?.list}
            initialState={paySignErrorInitialState}
            setPaysignErrorPopup={setPaysignErrorPopup}
            ConfirmButtonText={MODAL_POPUP_ACTIONS.OK_UPPERCASE}
          />
        </Modal>
      )}
    </React.Fragment>
  ))
}

const PAPCaptureInsuranceTask: React.FC<{
  recordId: string
  task: WorkflowInstanceTaskType
  isAppealTask?: boolean
}> = ({ recordId, task, isAppealTask = false }) => {
  const programPermissions = useAppSelector(
    (state: RootState) => state.clientPermissions?.programPermissions
  )
  const caseData = useAppSelector(
    (state: RootState) => state.workflowData?.caseData
  )
  const userId = useAppSelector((state: RootState) => state?.authUser?.user?.id)
  const siteSettings = useAppSelector((state) => state?.authUser?.site_settings)
  const methods = useForm()
  const { getValues, reset, setValue } = methods
  const [poiNotProvided, setPoiNotProvided] = useState<boolean>(false)
  const [isPoiHidden, setIsPoiHidden] = useState<boolean>(false)

  const hasEditPermission = (task: WorkflowInstanceTaskType) => {
    const hasServicePermission = permissionObject.Manage_Workflow(
      programPermissions,
      caseData,
      task,
      userId ?? ''
    )
    const hasIdentifierKey = task?.identifier?.task_permission
    if (hasIdentifierKey) {
      const hasTaskEditPermission =
        programPermissions[
          caseData?.programs?.id ?? workflowDefinition?.program_id ?? ''
        ]?.[hasIdentifierKey]?.Manage === true
      return hasTaskEditPermission
    } else {
      return hasServicePermission
    }
  }
  const disableAction = useRef<boolean>(
    isAppealTask || !hasEditPermission(task)
      ? true
      : task?.state !== WORKFLOW_STATUS.OPEN
  )
  const [isDiscrepancy, setIsDiscrepancy] = useState<boolean>(false)
  const [poiIsMissing, setPoiIsMissing] = useState<boolean>(false)

  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )

  const [subtotalDataObject, setSubtotalDataObject] = useState<FieldValues>({})

  const fetchRef = useRef(false)

  const savePoiFromHouseHold = async ({
    poi,
    papId,
  }: {
    poi: number
    papId: number
  }) => {
    try {
      const saveObject = {
        entity_id: workflowDefinition?.global_variables?.entity_id,
        id: recordId,
        [ENTITY_FIELDS.case.case_pap_details]: {
          id: papId,
          [ENTITY_FIELDS.CASE_PAP_DETAILS.household_size_poi]: poi,
        },
      }
      const response = await editEntityData(
        ENTITY_NAME.CASE,
        (workflowDefinition?.global_variables
          ?.entity_id as unknown as number) ?? 32,
        (workflowDefinition?.global_variables?.left_split
          ?.edit_layout_id as unknown as number) ?? 84,
        saveObject,
        recordId?.toString() ?? ''
      )

      if (response?.data) {
        calculateAndFetch()
      }
    } catch (err) {
      console.error('Error in savePoiFromHouseHold:', err)
    }
  }

  const calculateAndFetch = async (isFPLFetch = false) => {
    try {
      const res = await calculateFPL(recordId?.toString() ?? '')
      if (res?.data) {
        setIsDiscrepancy(!!res?.data?.is_discrepancy)

        fetchData(isFPLFetch)
      }
    } catch (err) {
      console.error('Error calculating FPL:', err)
    }
  }

  const fetchData = async (isFPLFetch = false) => {
    if (!recordId) return
    const payload = isFPLFetch
      ? {
          entity_id:
            (workflowDefinition?.global_variables
              ?.entity_id as unknown as number) ?? 32,
          select: [ENTITY_FIELDS.case.case_pap_details],
          page: 0,
          size: 25,
          related_entities: {
            [ENTITY_FIELDS.case.case_pap_details]: {
              select: [
                ENTITY_FIELDS.CASE_PAP_DETAILS.fpl_percentage_application,
                ENTITY_FIELDS.CASE_PAP_DETAILS.annual_income_on_application,
                ENTITY_FIELDS.CASE_PAP_DETAILS.household_size_poi,
                ENTITY_FIELDS.CASE_PAP_DETAILS.annual_income_poi,
                ENTITY_FIELDS.CASE_PAP_DETAILS.fpl_percentage_poi,
                ENTITY_FIELDS.CASE_PAP_DETAILS.fpl_percentage_final,
                ENTITY_FIELDS.CASE_PAP_DETAILS.household_size_final,
                ENTITY_FIELDS.CASE_PAP_DETAILS.annual_income_final,
                ENTITY_FIELDS.CASE_PAP_DETAILS.household_size_application,
                'poi_not_provided',
              ],
              related_entities: {},
            },
          },
        }
      : {
          entity_id:
            (workflowDefinition?.global_variables
              ?.entity_id as unknown as number) ?? 32,
          select: [
            ENTITY_FIELDS.case.case_pap_details,
            ENTITY_FIELDS.case.patient,
            ENTITY_FIELDS.case.c_case_income_details_case,
            ENTITY_FIELDS.case.c_medical_expense_details_case,
          ],
          page: 0,
          size: 25,
          related_entities: {
            [ENTITY_FIELDS.case.case_pap_details]: {
              select: [
                ENTITY_FIELDS.CASE_PAP_DETAILS.fpl_percentage_application,
                ENTITY_FIELDS.CASE_PAP_DETAILS.annual_income_on_application,
                ENTITY_FIELDS.CASE_PAP_DETAILS.household_size_poi,
                ENTITY_FIELDS.CASE_PAP_DETAILS.annual_income_poi,
                ENTITY_FIELDS.CASE_PAP_DETAILS.fpl_percentage_poi,
                ENTITY_FIELDS.CASE_PAP_DETAILS.fpl_percentage_final,
                ENTITY_FIELDS.CASE_PAP_DETAILS.household_size_final,
                ENTITY_FIELDS.CASE_PAP_DETAILS.annual_income_final,
                ENTITY_FIELDS.CASE_PAP_DETAILS.household_size_application,
                'poi_not_provided',
              ],
              related_entities: {},
            },
            [ENTITY_FIELDS.case.c_case_income_details_case]: {
              select: [ENTITY_FIELDS.c_case_income_details_case.annual_amount],
            },
            [ENTITY_FIELDS.case.c_medical_expense_details_case]: {
              select: [ENTITY_FIELDS.c_medical_expense_details_case.amount],
            },
          },
          filters: [
            {
              criteria: [],
            },
          ],
        }

    try {
      const response = await getGenericEntityListingAPI(
        payload as unknown as GetGenericEntityListingApiType,
        recordId
      )

      if (response) {
        if (
          !response?.[ENTITY_FIELDS.case.case_pap_details]?.[
            ENTITY_FIELDS.CASE_PAP_DETAILS.annual_income_poi
          ] &&
          Number(
            response?.[ENTITY_FIELDS.case.case_pap_details]?.[
              ENTITY_FIELDS.CASE_PAP_DETAILS.fpl_percentage_final
            ]
          ) > 300
        ) {
          setPoiIsMissing(true)
        } else {
          setPoiIsMissing(false)
        }
        setPoiNotProvided(
          response?.[ENTITY_FIELDS.case.case_pap_details]?.poi_not_provided ??
            false
        )
        if (isFPLFetch) {
          setValue(
            ENTITY_FIELDS.case.case_pap_details,
            response?.[ENTITY_FIELDS.case.case_pap_details]
          )
        } else {
          if (
            !response?.[ENTITY_FIELDS.case.case_pap_details]?.[
              ENTITY_FIELDS.CASE_PAP_DETAILS.household_size_poi
            ] &&
            response?.[ENTITY_FIELDS.case.case_pap_details]?.[
              ENTITY_FIELDS.CASE_PAP_DETAILS.household_size_application
            ] &&
            response?.[ENTITY_FIELDS.case.c_case_income_details_case]?.length >
              0
          ) {
            await savePoiFromHouseHold({
              poi: response?.[ENTITY_FIELDS.case.case_pap_details]?.[
                ENTITY_FIELDS.CASE_PAP_DETAILS.household_size_application
              ],
              papId: response?.[ENTITY_FIELDS.case.case_pap_details]?.id,
            })
            return
          }

          let annualammount = -1
          if (
            !isEmpty(response?.[ENTITY_FIELDS.case.c_case_income_details_case])
          ) {
            const annualAmountSubtotal =
              response?.[ENTITY_FIELDS.case.c_case_income_details_case]?.reduce(
                (
                  sum: number,
                  {
                    [ENTITY_FIELDS.c_case_income_details_case.annual_amount]:
                      annual_amount,
                  }: FieldValues
                ) => {
                  const v = Number(annual_amount)
                  return isNaN(v) ? sum : sum + v
                },
                0
              ) ?? 0

            annualammount = annualAmountSubtotal
          }
          let total = 0
          if (
            !isEmpty(
              response?.[ENTITY_FIELDS.case.c_medical_expense_details_case]
            )
          ) {
            const annualAmountSubtotal =
              response?.[
                ENTITY_FIELDS.case.c_medical_expense_details_case
              ]?.reduce(
                (
                  sum: number,
                  {
                    [ENTITY_FIELDS.c_medical_expense_details_case.amount]:
                      amount,
                  }: FieldValues
                ) => {
                  const v = Number(amount)
                  return isNaN(v) ? sum : sum + v
                },
                0
              ) ?? 0

            total = annualAmountSubtotal
          }

          setSubtotalDataObject({
            [ENTITY_FIELDS.c_medical_expense_details_case.amount]: total,
            [ENTITY_FIELDS.c_case_income_details_case.annual_amount]:
              annualammount,
          })
          reset({
            [ENTITY_FIELDS.case.case_pap_details]:
              response?.[ENTITY_FIELDS.case.case_pap_details],
            [ENTITY_FIELDS.case.patient]:
              response?.[ENTITY_FIELDS.case.patient],
            __subtotal_income: {
              annualammount: annualammount,
              amount: total,
            },
          })
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const saveAndFetch = async ({
    fieldName,
    value,
  }: {
    fieldName: string
    value: string
  }) => {
    try {
      const saveObject = {
        entity_id: workflowDefinition?.global_variables?.entity_id,
        id: recordId,
        [ENTITY_FIELDS.case.case_pap_details]: {
          id: getValues(`${ENTITY_FIELDS.case.case_pap_details}.id`),
          [fieldName]: value,
        },
      }

      const response = await editEntityData(
        ENTITY_NAME.CASE,
        workflowDefinition?.global_variables?.entity_id as unknown as number,
        workflowDefinition?.global_variables?.left_split
          ?.edit_layout_id as unknown as number,
        saveObject,
        recordId
      )

      if (response?.data) {
        calculateAndFetch(true)
      }
    } catch (err) {
      console.error('Error in saveAndFetch:', err)
    }
  }

  const saveDiscrepancy = async (incomeObject: FieldValues) => {
    try {
      const papId = getValues(`${ENTITY_FIELDS.case.case_pap_details}.id`)

      const savePayload = {
        entity_id: workflowDefinition?.global_variables?.entity_id,
        id: recordId,
        [ENTITY_FIELDS.case.case_pap_details]: {
          id: papId,
          annual_income_final: incomeObject?.annual_income,
          household_size_final: incomeObject?.household_size,
          fpl_percentage_final: incomeObject?.fpl,
          poi_not_provided: poiNotProvided,
          agent_income_priority: incomeObject?.priority,
        },
      }
      const response = await editEntityData(
        ENTITY_NAME.CASE,
        (workflowDefinition?.global_variables
          ?.entity_id as unknown as number) ?? 32,
        (workflowDefinition?.global_variables?.left_split
          ?.edit_layout_id as unknown as number) ?? 84,
        savePayload,
        recordId?.toString() ?? ''
      )
      if (response?.data) {
        setIsDiscrepancy(false)
        setValue(
          `${ENTITY_FIELDS.case.case_pap_details}.${ENTITY_FIELDS.CASE_PAP_DETAILS.household_size_final}`,
          incomeObject?.household_size
        )
        setValue(
          `${ENTITY_FIELDS.case.case_pap_details}.${ENTITY_FIELDS.CASE_PAP_DETAILS.annual_income_final}`,
          incomeObject?.annual_income
        )
        setValue(
          `${ENTITY_FIELDS.case.case_pap_details}.${ENTITY_FIELDS.CASE_PAP_DETAILS.fpl_percentage_final}`,
          incomeObject?.fpl
        )
      }
    } catch (err) {
      console.error('Error in saveDiscrepancy:', err)
    }
  }

  const resolveDiscrepancy = async () => {
    try {
      const utcDateString = new Date().toISOString()
      const fetchPayload = {
        entity_id:
          (workflowDefinition?.global_variables
            ?.entity_id as unknown as number) ?? 32,
        select: [ENTITY_FIELDS.case.patient],
        page: 0,
        size: 25,
        related_entities: {
          [ENTITY_FIELDS.case.patient]: {
            select: ['c_patient_income_details_patient'],
            related_entities: {
              c_patient_income_details_patient: {
                select: [
                  'household_size',
                  'annual_income',
                  'fpl_percentage',
                  'created_at',
                  'effective_end_date',
                  'error_message',
                ],
                filters: [
                  {
                    criteria: [
                      {
                        field_name: 'effective_end_date',
                        operation: 'IS_AFTER_OR_EQUAL_TO',
                        values: [utcDateString],
                      },
                    ],
                  },
                ],
              },
            },
          },
        },
        filters: [
          {
            criteria: [],
          },
        ],
      }
      const response = await getGenericEntityListingAPI(
        fetchPayload as unknown as GetGenericEntityListingApiType,
        recordId
      )
      if (response) {
        if (
          response?.[ENTITY_FIELDS.case.patient]
            ?.c_patient_income_details_patient?.length > 0
        ) {
          const incomeObject = {
            fpl: response?.[ENTITY_FIELDS.case.patient]
              ?.c_patient_income_details_patient?.[0]?.fpl_percentage,
            household_size:
              response?.[ENTITY_FIELDS.case.patient]
                ?.c_patient_income_details_patient?.[0]?.household_size,
            annual_income:
              response?.[ENTITY_FIELDS.case.patient]
                ?.c_patient_income_details_patient?.[0]?.annual_income,
            priority: PAP_NAMESPACES?.EXPERIAN_UPPERCASE,
          }
          await saveDiscrepancy(incomeObject)
        }
      }
    } catch (err) {
      console.error('Error in resolveDiscrepancy:', err)
    }
  }

  const onOptionSelect = async (key: { [key: string]: string }) => {
    if (key?.id === 'experian') {
      await resolveDiscrepancy()
      return
    } else {
      const incomeObject = {
        fpl: getValues(
          `${ENTITY_FIELDS.case.case_pap_details}.${PAP_CONSTANTS.FPL_PERCENTAGE_POI}`
        ),
        household_size: getValues(
          `${ENTITY_FIELDS.case.case_pap_details}.${PAP_CONSTANTS.HOUSEHOLD_SIZE_POI}`
        ),
        annual_income: getValues(
          `${ENTITY_FIELDS.case.case_pap_details}.${PAP_CONSTANTS.ANNUAL_INCOME_POI}`
        ),
        priority: PAP_NAMESPACES?.POI_UPPERCASE,
      }
      await saveDiscrepancy(incomeObject)
    }
  }

  const poiNotProvidedValue = async () => {
    const poi = !poiNotProvided
    setPoiNotProvided(!poiNotProvided)
    if (!isDiscrepancy) {
      try {
        const papId = getValues(`${ENTITY_FIELDS.case.case_pap_details}.id`)

        const savePayload = {
          entity_id: workflowDefinition?.global_variables?.entity_id,
          id: recordId,
          [ENTITY_FIELDS.case.case_pap_details]: {
            id: papId,
            poi_not_provided: poi,
          },
        }
        await editEntityData(
          ENTITY_NAME.CASE,
          (workflowDefinition?.global_variables
            ?.entity_id as unknown as number) ?? 32,
          (workflowDefinition?.global_variables?.left_split
            ?.edit_layout_id as unknown as number) ?? 84,
          savePayload,
          recordId?.toString() ?? ''
        )
      } catch (error) {
        console.error('Error in poiNotProvidedValue:', error)
      }
    }
  }

  useEffect(() => {
    if (
      subtotalDataObject?.[
        ENTITY_FIELDS.c_medical_expense_details_case.amount
      ] === undefined ||
      subtotalDataObject?.[
        ENTITY_FIELDS.c_case_income_details_case.annual_amount
      ] === undefined
    )
      return
    const amount = getValues('__subtotal_income.amount') || 0
    const annualammount = getValues('__subtotal_income.annualammount') || 0

    if (
      subtotalDataObject?.[
        ENTITY_FIELDS.c_medical_expense_details_case.amount
      ] !== amount ||
      subtotalDataObject?.[
        ENTITY_FIELDS.c_case_income_details_case.annual_amount
      ] !== annualammount ||
      fetchRef.current === false
    ) {
      if (task?.state === WORKFLOW_STATUS.COMPLETED || isAppealTask) {
        fetchData(false)
      } else {
        calculateAndFetch()
      }
      fetchRef.current = true
    }
  }, [subtotalDataObject])

  useEffect(() => {
    const ishidden: SiteSettingsType =
      siteSettings?.find(
        (item: SiteSettingsType) =>
          item?.key === PAP_WORKFLOW_CONSTANTS.HIDE_EXCEPTION_CHECKBOX
      ) ?? {}
    setIsPoiHidden((ishidden?.value as boolean) ?? false)
  }, [siteSettings])
  return (
    <>
      <div className={`flex justify-end mr-4 ${!isAppealTask ? 'my-5' : ''}`}>
        <div className="flex gap-3 items-center">
          {poiIsMissing && !isPoiHidden ? (
            <div className="text-sm text-red-600 mb-2 text-right">
              <Checkbox
                name="poi_not_provided"
                checked={poiNotProvided}
                label={STRINGS?.POI_NOT_PROVIDED}
                onChange={poiNotProvidedValue}
                disabled={disableAction?.current}
              />
            </div>
          ) : null}

          {isDiscrepancy ? (
            <div className="max-w-32">
              <SelectButtonDropDown
                dropdownItemList={[
                  {
                    id: PAP_CONSTANTS?.POI,
                    name: PAP_NAMESPACES?.PROOF_OF_INCOME,
                  },
                  {
                    id: PAP_CONSTANTS?.EXPERIAN,
                    name: PAP_NAMESPACES?.EXPERIAN,
                  },
                ]}
                onOptionSelect={onOptionSelect}
                isButtonOnTop={true}
                isPink
                buttonText={PAP_NAMESPACES?.SELECT_PRIORITY}
              />
            </div>
          ) : null}
        </div>
      </div>

      <FormProvider {...methods}>
        <form>
          <div className="space-y-4" data-component="IncomeSummarySections">
            {IncomeContainerList.map((sectionKey) => {
              if (sectionKey?.type === 'layout') {
                return (
                  <IncomeTaskTablesComponent
                    layoutInfo={
                      (
                        task?.identifier?.layout_info as Record<
                          string,
                          WorkflowTaskIdentifierEntityLayoutInfoType | undefined
                        >
                      )?.[(sectionKey?.layoutInfoKey as string) ?? '']
                    }
                    section={sectionKey}
                    recordId={recordId}
                    parentKey={sectionKey?.key}
                    setSubtotalDataObject={setSubtotalDataObject}
                    calculateAndFetch={calculateAndFetch}
                    key={sectionKey?.layoutId}
                    disableAction={disableAction?.current}
                    task={task}
                    isAppealTask={isAppealTask}
                  />
                )
              }
              const section = IncomeConstants[sectionKey?.key ?? '']
              if (!section) return null

              return (
                <IncomeSectionComponent
                  section={section}
                  fetchAgain={saveAndFetch}
                  disableAction={disableAction?.current}
                  key={section.id}
                  isDiscrepancy={isDiscrepancy}
                  subUiIdentifier={task?.identifier?.sub_ui_identifier ?? ''}
                />
              )
            })}
          </div>
        </form>
      </FormProvider>
    </>
  )
}

export default PAPCaptureInsuranceTask
